<template>
    <shift-loader
        name='resources'
        @loaded='loadData'
        :showPageFeatures='enablePageFeatures'
        :hidePageFeatures='disablePageFeatures'
        :isLoading="$wait.is('get data')"
    >
        <template #default='{ isCurrentShiftOwner }'>
            <v-container fluid class='relative ma-0 px-3 py-0 fill-height' style='overflow: hidden'>
                <h1 class='printonly title'>{{ $t('resources.heading.resources') }}, {{ $route.params.date | formatDate }}</h1>
                <v-row class='ma-0 page-layout'>
                    <v-col
                        :cols='4'
                        :sm='4'
                        :md='4'
                        :lg='3'
                        :xl='2'
                        class='pr-2 full-height clear-bottom d-flex flex-column'
                        style='overflow-x: hidden; overflow-y: hidden; max-width: 250px'
                    >
                        <v-container>
                            <v-row>
                                <v-col :cols='2'>
                                    <h3 class='title mb-0'>{{ $t('resources.heading.crews') }}</h3>
                                </v-col>
                                <v-col :cols='10'>
                                    <v-select
                                        ref="crewSelect"
                                        :disabled="!isCurrentShiftOwner || $wait.is('set_selected_crews')"
                                        :readonly='!isCurrentShiftOwner'
                                        style='max-width: 250px'
                                        :value='crewIds'
                                        :items='crewSelect'
                                        item-value='id'
                                        item-text='name'
                                        class='mt-0 pt-0 ml-10'
                                        hide-details
                                        @change='debouncedUpdateShiftSelectedCrews'
                                        :multiple='true'
                                        :loading="$wait.is('set_selected_crews')"
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                        <v-divider />
                        <v-container v-if='noCrewSelected' class='ma-0 pa-0 fill-height pb-4 justify-center'>
                            <empty-state icon='mdi-account-off' :message="$t('resources.message.no-crew-selected')" />
                        </v-container>
                        <v-container v-else class='ma-0 pa-0 pb-4' style='overflow-y: auto'>
                            <v-row class='my-1 pa-0' v-for='(role, index) in roles' :key='role.id'>
                                <v-col cols='12' class='py-0'>
                                    <v-card :elevation='1' :class='{ striped: index % 2 == 1 }' outlined class='pa-2'>
                                        <v-card-title class='subtitle-1 pl-1 pr-0 py-2'>
                                            <strong>{{ role.name }}</strong>
                                            <v-spacer />
                                            <v-btn
                                                small
                                                outlined
                                                primary
                                                :elevation='0'
                                                @click='manuallyAddCrew(role)'
                                                :disabled='isCurrentShiftOwner === false'
                                            >{{ $t('shared.button.add') }}
                                            </v-btn
                                            >
                                        </v-card-title>
                                        <div
                                            v-for='crew in getMembersForRoleByCrew(role.id)'
                                            :key="crew.id + '-' + role.id"
                                        >
                                            <v-card :elevation='0' class='pa-1 mb-1'>
                                                <div class='body-2'>
                                                    {{ crew.name }}
                                                </div>
                                                <div v-for='member in crew.staff' :key='member.id'>
                                                    <shift-member
                                                        :staff='member'
                                                        :isReadOnly='isCurrentShiftOwner === false'
                                                        :showUtilisation='isOnline'
                                                        :tooltime-limits='staffRoleTooltimeLimits'
                                                        :shift-length-in-minutes='shiftLengthInMinutes'
                                                        @dragStart='startDragStaff'
                                                        @dragEnd='endDrag'
                                                        @touchDrop='onStaffTouchDrop'
                                                        @scheduleClick='viewLeaveFor(member, true)'
                                                        @addClick='onAddCrewMember(member)'
                                                        @removeClick='onRemoveCrewMember(member)'
                                                    />
                                                </div>
                                            </v-card>
                                        </div>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-row class='my-2 mx-1 pa-0'>
                                <v-btn
                                    small
                                    color='secondary'
                                    class='flex-grow-1'
                                    :elevation='0'
                                    :disabled='isCurrentShiftOwner === false'
                                    @click='addCrewRole'
                                >{{ $t('resources.button.add-crew-role') }}
                                </v-btn
                                >
                            </v-row>
                        </v-container>
                    </v-col>
                    <v-divider vertical></v-divider>
                    <v-col
                        :cols='6'
                        :sm='6'
                        :md='7'
                        :lg='8'
                        :xl='9'
                        class='full-height clear-bottom'
                        style='overflow-x: hidden; overflow-y: hidden'
                    >
                        <v-row style='min-height: 70px'>
                            <v-col :cols='12'>
                                <div class='d-flex align-center'>
                                    <h4 class="text-h4 mb-3 d-flex">{{ $t('resources.heading.assigned-resources') }}</h4>
                                    <v-spacer></v-spacer>
                                    <div class='d-flex align-center'>
                                        <v-menu
                                            v-if='showCopyPreviousButton'
                                            v-model='copyPreviousAllocationsMenu'
                                            :close-on-content-click='false'
                                            offset-y
                                        >
                                            <template v-slot:activator='{ on: menu, attrs }'>
                                                <v-tooltip bottom>
                                                    <template v-slot:activator='{ on: tooltip }'>
                                                        <v-access-control
                                                            :useCustom='!isCurrentShiftOwner'
                                                            :offlineAccess='false'
                                                        >
                                                            <template #custom>

                                                            </template>
                                                            <v-btn
                                                                color='secondary'
                                                                small
                                                                class='mr-1 float-right mb-0 ml-1'
                                                                v-bind='attrs'
                                                                :loading="$wait.is('copy equipment allocations - info')"
                                                                :disabled='copyPreviousAllocationsInfo == null || !copyPreviousAllocationsInfo.canCopy'
                                                                v-on='{ ...tooltip, ...menu }'
                                                            >
                                                                <v-icon left>mdi-content-copy
                                                                </v-icon
                                                                >
                                                                {{ $t('resources.button.copy-previous') }}
                                                            </v-btn>
                                                        </v-access-control>
                                                    </template>
                                                    <span>{{ $t('resources.copy-previous-allocations') }}</span>
                                                </v-tooltip>
                                            </template>
                                            <v-card v-if='copyPreviousAllocationsInfo'>
                                                <v-list>
                                                    <v-list-item>
                                                        <v-list-item-content>
                                                            <v-list-item-title>{{ $t('resources.copy-previous-shift-allocations') }}
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>Copy from
                                                                {{ copyPreviousAllocationsInfo.copyShiftName }}
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>
                                                <v-divider></v-divider>
                                                <v-list>
                                                    <v-list-item>
                                                        <v-list-item-content>
                                                            This will copy
                                                            {{ copyPreviousAllocationsInfo.countOfEquipmentThatCanPopulate
                                                            }} {{ 'equipment' | term }}
                                                            {{ copyPreviousAllocationsInfo.countOfEquipmentThatCanPopulate | pluralize('allocation', 'allocations')
                                                            }} and
                                                            {{ copyPreviousAllocationsInfo.countOfRolesThatCanPopulate
                                                            }} role
                                                            {{ copyPreviousAllocationsInfo.countOfRolesThatCanPopulate | pluralize('allocation', 'allocations')
                                                            }}.
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>

                                                <v-card-actions>
                                                    <v-spacer></v-spacer>

                                                    <v-btn color='gray' text
                                                           @click='() => copyPreviousAllocationsMenu = false'>No
                                                    </v-btn>
                                                    <v-btn role='button' color='primary' @click='copyPreviousAllocation'
                                                           text
                                                    >
                                                        <v-icon>mdi-check</v-icon>
                                                        {{ $t('resources.yes') }}
                                                    </v-btn>
                                                </v-card-actions>
                                            </v-card>
                                        </v-menu>
                                        <v-switch class='mr-2' dense v-model='hideUnavailableEquipment'
                                                  :label="$t('resources.hide-unavailable-term', { term: $termSync('Equipment') })"></v-switch>
                                        <v-access-control
                                            v-if='showClearStaffAssignments'
                                            :useCustom='!isCurrentShiftOwner'
                                            :offlineAccess='false'
                                        >
                                            <template #custom>
                                                <no-permission-btn :label="$t('resources.button.clear-staff')" />
                                            </template>
                                            <v-btn
                                                color='secondary'
                                                small
                                                @click='clearStaffAssignments()'
                                                class='mr-1 float-right mb-0 ml-1'
                                            >
                                                <v-icon left>mdi-close
                                                </v-icon
                                                >
                                                {{ $t('resources.button.clear-staff') }}
                                            </v-btn>
                                        </v-access-control>
                                        <v-text-field
                                            outlined
                                            v-model='equipmentFilter'
                                            :label="$t('shared.label.filter')"
                                            clearable
                                            class='mb-0'
                                            style='max-width: 200px'
                                            hide-details
                                            dense
                                        />
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                        <v-divider />
                        <v-container
                            fluid
                            class='ma-0 pt-2 fill-height pb-4 align-content-space-between'
                            style='overflow-y: auto'
                        >
                            <v-row v-if='hasShiftCommsRoles' class='ma-0 pb-2'>
                                <v-col :cols='12'>
                                    <h4 class="text-h4 d-flex mb-3">{{ $t('resources.heading.assigned-roles') }}</h4>
                                </v-col>
                                <v-col
                                    :key='roleField.id' v-for='roleField in shiftCommsRoles'
                                    :md='6'
                                    :lg='4'
                                    :xl='3'
                                    :sm='12'
                                    :cols='12'
                                    class='pa-0'
                                >
                                    <transition name='fade' appear>
                                        <role-card
                                            :role-value='roleField'
                                            :isReadOnly='isCurrentShiftOwner === false'
                                            :isActive='dropTargetId === roleField.fieldId'
                                            :possible-staff-names='staffNamesAssignableToRoles'
                                            @addStaff='onAddStaffToRole'
                                            @inActiveZone='onAddRoleDropTarget'
                                            @outOfActiveZone='onRemoveDropTarget' />
                                    </transition>
                                </v-col>
                            </v-row>
                            <v-row v-if='showEquipmentLocations && hasVisibleEquipmentLocations' class='ma-0 pb-2'>
                                <v-col :cols='12'>
                                    <h4 class="text-h4 d-flex mb-3">{{ locationPluralName }}</h4>
                                </v-col>
                                <v-col
                                    :key='taskLocation.id' v-for='taskLocation in wrappedEquipmentLocations'
                                    :md='12'
                                    :lg='12'
                                    :xl='6'
                                    :sm='12'
                                    :cols='12'
                                    class='pa-1 my-2'
                                >
                                    <transition name='fade' appear>
                                        <location-tasks-card
                                            :isReadOnly='isCurrentShiftOwner === false'
                                            :isActive='dragSource === DragSourceType.STAFF && (dropTargetId === taskLocation.id || secondaryDropTargetId === taskLocation.id)'
                                            :active-task='getActiveTargetTask(taskLocation.id)'
                                            :shiftId='shiftId'
                                            :location='taskLocation'
                                            :dragging-levelled-resource="draggingLevelledResource"
                                            :divide-staff-by-levelled-resource='true'
                                            @inActiveZoneStaff='onAddLocationDropTarget'
                                            @outOfActiveZoneStaff='onRemoveDropTarget'
                                            @inActiveZoneTask='onAddLocationTaskDropTarget'
                                            @outOfActiveZoneTask='onRemoveDropTarget'
                                            @addStaffToTask='(taskId,staffId)=>onAddStaffToTask(taskId, taskLocation.plannedLocationId, staffId)'
                                            @addStaffToLocation='(staffId)=>onAddStaffToLocation(taskLocation.plannedLocationId, staffId)'
                                            @removeStaffFromTask='(taskId,staffId) => onRemoveStaffFromTask(taskId, taskLocation.plannedLocationId, staffId)'
                                            @removeStaffFromLocation='(staffId)=>onRemoveStaffFromLocation(taskLocation.plannedLocationId, staffId)'
                                            @changeTaskComment='(taskId,comment)=>onChangeTaskComment(taskLocation.plannedLocationId, taskId, comment)'
                                            @changeLocationComment='(comment)=>onChangeLocationComment(taskLocation.plannedLocationId, comment)'
                                            @hazardsClick='()=>onOpenLocationHazards(taskLocation.plannedLocationId)'
                                        />
                                    </transition>
                                </v-col>
                            </v-row>
                            <v-row v-if="showEquipmentGroupedByLocation && hasVisibleLocationsWithEquipment" class='ma-0 pb-2'>
                                <v-col :cols='12'>
                                    <h4 class="text-h4 d-flex mb-3">{{ locationPluralName }}</h4>
                                </v-col>
                                <v-col
                                    :key='equipmentLocation.id' v-for='equipmentLocation in wrappedLocations'
                                    :md='12'
                                    :lg='12'
                                    :xl='12'
                                    :sm='12'
                                    :cols='12'
                                    class='pa-1 my-2'
                                >
                                    <transition name='fade' appear>
                                        <location-with-equipment-card
                                            :isReadOnly='isCurrentShiftOwner === false'
                                            :isActive='dragSource === DragSourceType.STAFF && (dropTargetId === equipmentLocation.id)'
                                            :shiftId='shiftId'
                                            :location='equipmentLocation'
                                            :divide-staff-by-equipment='true'
                                            @inActiveZoneStaff='onAddLocationDropTarget'
                                            @outOfActiveZoneStaff='onRemoveDropTarget'
                                            @outOfActiveZoneTask='onRemoveDropTarget'
                                            @addStaffToLocation='(staffId)=>onAddStaffToLocation(equipmentLocation.plannedLocationId, staffId)'
                                            @removeStaffFromLocation='(staffId)=>onRemoveStaffFromLocation(equipmentLocation.plannedLocationId, staffId)'
                                            @changeLocationComment='(comment)=>onChangeLocationComment(equipmentLocation.plannedLocationId, comment)'
                                            @hazardsClick='()=>onOpenLocationHazards(equipmentLocation.plannedLocationId)'
                                        >
                                            <template #equipment-card="{ equip }">
                                                <equipment-card
                                                    :equipment='equip'
                                                    :shiftId='shiftId'
                                                    :isReadOnly='isCurrentShiftOwner === false'
                                                    :isActive='dropTargetId === equip.id'
                                                    :is-in-workshop='equipmentInWorkshop.includes(equip.equipmentId)'
                                                    :is-needed-in-workshop='equipmentToWorkshop.includes(equip.equipmentId)'
                                                    @inActiveZone='onAddEquipmentDropTarget'
                                                    @outOfActiveZone='onRemoveDropTarget'
                                                    @servicesClick='onOpenEquipmentServices'
                                                    @hazardsClick='onOpenEquipmentHazards'
                                                    @inWorkshopClick='toggleInWorkshop'
                                                    @neededInWorkshopClick='toggleNeededInWorkshop'
                                                    @addStaff='onAddStaffToEquipment'
                                                    @removeStaffMember='onRemoveStaffFromEquipment'
                                                    @addSecondaryEquipment='onAddSecondaryEquipmentToEquipment'
                                                    @removeSecondaryEquipment='onRemoveSecondaryEquipmentFromEquipment'
                                                    @changeEquipmentLocation='setEquipLocationFor'
                                                    @changeEquipmentComment='setEquipCommentsFor'
                                                />
                                            </template>
                                        </location-with-equipment-card>
                                    </transition>
                                </v-col>
                            </v-row>
                            <v-divider></v-divider>
                            <template v-for='(group, index) in groupedEquipment'>
                                <v-row class='ma-0 pa-0' :key='index' v-if='group.showHeader'>
                                    <v-col :cols='12'>
                                        <h4 class="text-h4 align-center d-flex mb-3">{{ $t(group.translationKey) }} <v-spacer></v-spacer> <v-switch dense :label="$t('resources.show-unused')" :input-value="group.showUnassigned" @change="group.setShowUnassigned"></v-switch></h4>
                                    </v-col>
                                    <v-col cols='12' v-if="group.data.length === 0" class="d-flex flex-row justify-center">
                                        <v-subheader class="overline font-weight-bold">{{ $t('resources.label.equipment-filtered') }}</v-subheader>
                                    </v-col>
                                    <v-col
                                        :key='equip.id' v-for='equip in group.data'
                                        :md='6'
                                        :lg='4'
                                        :xl='3'
                                        :sm='12'
                                        :cols='12'
                                        class='pa-0'
                                    >
                                        <transition name='fade' appear>
                                            <equipment-card
                                                :equipment='equip'
                                                :shiftId='shiftId'
                                                :isReadOnly='isCurrentShiftOwner === false'
                                                :isActive='dropTargetId === equip.id'
                                                :is-in-workshop='equipmentInWorkshop.includes(equip.equipmentId)'
                                                :is-needed-in-workshop='equipmentToWorkshop.includes(equip.equipmentId)'
                                                @inActiveZone='onAddEquipmentDropTarget'
                                                @outOfActiveZone='onRemoveDropTarget'
                                                @servicesClick='onOpenEquipmentServices'
                                                @hazardsClick='onOpenEquipmentHazards'
                                                @inWorkshopClick='toggleInWorkshop'
                                                @neededInWorkshopClick='toggleNeededInWorkshop'
                                                @addStaff='onAddStaffToEquipment'
                                                @removeStaffMember='onRemoveStaffFromEquipment'
                                                @addSecondaryEquipment='onAddSecondaryEquipmentToEquipment'
                                                @removeSecondaryEquipment='onRemoveSecondaryEquipmentFromEquipment'
                                                @changeEquipmentLocation='setEquipLocationFor'
                                                @changeEquipmentComment='setEquipCommentsFor'
                                            />
                                        </transition>
                                    </v-col>
                                </v-row>
                            </template>
                        </v-container>
                    </v-col>
                    <v-col v-if='isCurrentShiftOwner' :cols='2' :md='1' class='full-height px-2 mid-col clear-bottom'>
                        <v-list dense class='pt-0'>
                            <v-subheader>{{ $t('resources.heading.secondary-equipment') }}</v-subheader>
                            <v-text-field
                                outlined
                                v-model='secondaryEquipmentFilter'
                                :label="$t('shared.label.filter')"
                                clearable
                                class='mb-2'
                                hide-details
                                dense
                            />

                            <div v-for='(group, index) in filteredSortedEquipment' :key='index'>
                                <h3 class='subtitle-2' v-if='group.equipment.length'>{{ group.equipmentRole.name }}</h3>
                                <drag
                                    @dragstart='startDragEquipment'
                                    @dragend='endDrag'
                                    @touchdrop='onEquipmentTouchDrop(...arguments)'
                                    class='drag'
                                    v-for='equip in group.equipment'
                                    :key='equip.id'
                                    :transferData="{ type: 'equipment', equipment: equip }"
                                >
                                    <transition name='fade' appear>
                                        <v-list-item :id='equip.id'
                                                     class='py-0 px-0 clickable draggable equiplist mb-1'>
                                            <v-list-item-title class='overline px-0'>
                                                <v-img
                                                    :src='equip.imageUrl'
                                                    :max-width='60'
                                                    :max-height='42'
                                                    contain
                                                    class='avatar-image'
                                                />
                                                <span class='equipname'>{{ equip.name }}</span>
                                            </v-list-item-title>
                                        </v-list-item>
                                    </transition>
                                </drag>
                            </div>
                        </v-list>
                    </v-col>
                </v-row>
                <equipment-availability-dialog
                    v-if='selectedEquipmentAvailability != null'
                    :shiftId='shiftId'
                    :model='selectedEquipmentAvailability'
                    :isReadOnly='!isCurrentShiftOwner'
                    @cancel='selectedEquipmentAvailability = null'
                    @saved='onSaveAvailability'
                />
                <add-crew-role @selectRole='onAdditionalRoleSelected' ref='addCrewRole' />
                <add-any-staff
                    @addNewStaff='onAddNewStaffMember'
                    @selectStaff='onAdditionalStaffSelected'
                    ref='addAnyStaff'
                />
                <view-leave-overtime
                    :readonly='!isCurrentShiftOwner'
                    @scheduleUnplannedLeave='onUnplannedLeaveScheduled'
                    ref='viewLeaveOvertime'
                />
                <shift-printer
                    :shiftId='shiftId'
                    :departmentId='$route.params.dept'
                    :departmentName='departmentName'
                    :department-features='departmentFeatures'
                    :date='$route.params.date'
                    :shift='$route.params.shift'
                />
                <shift-reports
                    :shiftId='shiftId'
                    :departmentId='$route.params.dept'
                    :departmentName='departmentName'
                    :date='$route.params.date'
                    :shift='$route.params.shift'
                />
                <equipment-shift-hazard-notes-dialog
                    v-if='selectedHazardEquipment != null'
                    :equipment='selectedHazardEquipment'
                    :shift-id='shiftId'
                    @close='onCloseEquipmentHazards'
                >
                </equipment-shift-hazard-notes-dialog>
                <location-shift-hazard-notes-dialog
                    v-if="selectedHazardLocation"
                    :shift-row="selectedHazardLocation"
                    :shift-id='shiftId'
                    @close="onCloseLocationHazards"
                    @saved="onCloseLocationHazards"
                >

                </location-shift-hazard-notes-dialog>
                <confirm ref="confirm"></confirm>
            </v-container>
        </template>
    </shift-loader>
</template>

<script>
import { EventBus, Events } from '@/lib/EventBus';
import MineAreas from '@/lib/data/MineAreas';
import Staff from '@/lib/data/Staff';
import StaffRoles from '@/lib/data/StaffRoles';
import PlannedEquipment from '@/lib/data/PlannedEquipment';
import Shift from '@/lib/data/Shift';
import WeekCrews from '@/lib/data/WeekCrews';
import CurrentShiftStore from '@/lib/stores/CurrentShiftStore';
import AddCrewRole from './Dialogs/AddCrewRole.vue';
import AddAnyStaff from './Dialogs/AddAnyStaff.vue';
import ViewLeaveOvertime from './Dialogs/ViewLeaveOvertime.vue';
import EquipmentRoles from '@/lib/data/EquipmentRoles';
import EquipmentAvailabilityDialog from './SubComponents/EquipmentAvailabilityDialog.vue';
import EquipmentAvailability from '@/lib/data/EquipmentAvailability';
import UserStore from '@/lib/stores/UserStore';
import ModelBuilder from '@/lib/ModelBuilder';
import { showToastMessage } from '@/lib/Toast';
import { IsPrimary, IsSecondary, IsSupport, SortByRoleThenName } from '@/lib/services/Equipment';
import { useDepartmentStore } from '@/lib/stores/DepartmentStore';
import { useShiftWindowStore } from '@/lib/stores/ShiftWindowStore';
import { mapActions, mapState } from 'pinia';
import { useHeadingsStore } from '@/lib/stores/HeadingsStore';
import { useMineAreaStore } from '@/lib/stores/MineAreaStore';
import { useSystemStore } from '@/lib/stores/SystemStore';
import { useShiftCrew, useShiftEquipment, useShiftDetails, useShiftCommunication } from '@/lib/stores/Shift';
import { populateStoresForShift } from '@/lib/stores/DataManager';
import ShiftMember from '@/components/ShiftBoard/Resources/ShiftMember.vue';
import EquipmentCard from '@/components/ShiftBoard/Resources/EquipmentCard.vue';
import ShiftLoader from '@/components/ShiftBoard/ShiftLoader.vue';
import _ from 'lodash';
import { ShiftUtilisationPercentageDisplayGroup } from '@/models/client/shift-utilisation-percentage-display';
import EquipmentShiftHazardNotesDialog from '@/components/Shared/EquipmentShiftHazardNotesDialog.vue';
import {
    CommsFieldDataType,
    CommsFieldTemporalLevel,
    DepartmentFeatureFlags,
    UpdateShiftCommunicationsCommand
} from '@/models/api';
import { DragSourceType, DropTargetType } from '@/components/ShiftBoard/Resources/models';
import RoleCard from '@/components/ShiftBoard/Resources/RoleCard.vue';
import { DepartmentHasFeature } from '@/lib/services/Department';
import LocationTasksCard from '@/components/ShiftBoard/Resources/LocationTasksCard.vue';
import { BuildUpdateAdHocTaskCommandFromTask } from '@/lib/services/Location';
import LocationShiftHazardNotesDialog from '@/components/Shared/LocationShiftHazardNotesDialog.vue';
import ShiftLocationDetails from '@/lib/data/ShiftLocationDetails';
import Logging from '@/lib/Logging';
import LocationWithEquipmentCard from '@/components/ShiftBoard/Resources/LocationWithEquipmentCard.vue';
import TaskError from '@/components/Board/TaskError.vue';
import TaskNotifications from '@/components/Board/TaskNotifications.vue';
import ShiftTaskContents from '@/components/ShiftBoard/ShiftTaskContents.vue';

export default {
    components: {
        LocationWithEquipmentCard,
        LocationShiftHazardNotesDialog,
        LocationTasksCard,
        RoleCard,
        EquipmentShiftHazardNotesDialog,
        AddCrewRole,
        AddAnyStaff,
        ViewLeaveOvertime,
        EquipmentAvailabilityDialog,
        EquipmentCard,
        ShiftMember,
        ShiftLoader
    },
    data() {
        return {
            dragSource: null,
            dragData: null,
            dropTarget: null,
            dragElem: null,
            selectedShiftCrew: null,
            selectedEquipmentAvailability: null,
            showUnassignedEquipment: {
                primary: true,
                secondary: true,
                support: true
            },
            mine: null,
            equipmentFilter: '',
            secondaryEquipmentFilter: '',
            hideUnavailableEquipment: true,
            copyPreviousAllocationsInfo: null,
            copyPreviousAllocationsMenu: false,
            selectedHazardEquipment: null,
            selectedHazardLocation: null
        };
    },
    watch: {
        utilisationStatsDisplay(newValue, oldValue) {
            EventBus.$emit(Events.ShowStats, this.utilisationStatsDisplay);
        }
    },
    computed: {
        DragSourceType() {
            return DragSourceType
        },
        ...mapState(useDepartmentStore, ['departmentName', 'shiftOwnerId', 'levelledResources', 'departmentFeatures','locationName', 'locationPluralName']),
        ...mapState(useHeadingsStore, ['getTasksWithEquipmentByShift', 'headings']),
        ...mapState(useShiftDetails, ['shiftId', 'weekDate', 'primaryEquipmentUtilisation', 'secondaryEquipmentUtilisation', 'supportEquipmentUtilisation', 'crewUtilisation']),
        ...mapState(useShiftCommunication, ['shiftCommunication']),
        ...mapState(useMineAreaStore, [
            'firstShiftStartsAt',
            'activeShiftSchedule',
            'availableShifts',
            'firstDayOfWeek',
            'locale',
            'isSandpit',
            'shiftLengthInMinutes'
        ]),
        ...mapState(useSystemStore, [
            'staffMembers',
            'equipmentRoles',
            'staffRoles',
            'getStaffRoleById',
            'getStaffById',
            'isOnline'
        ]),
        ...mapState(useShiftEquipment, [
            'equipment',
            'equipmentById',
            'getSecondaryEquipmentByEquipment',
            'getStaffForEquipment',
            'equipmentInWorkshop',
            'equipmentToWorkshop'
        ]),
        ...mapState(useShiftCrew, ['crewIds', 'weekCrews', 'roles', 'staffOnShift']),
        crewIdToNameMap() {
            const map = {};

            this.weekCrews.forEach((weekCrew) => {
                map[weekCrew.crew.id] = weekCrew.crew.name;
            });

            return map;
        },
        getMembersForRoleByCrew(roleId) {
            return (roleId) => {
                const staffWithMatchingRole = this.staffOnShift.filter((x) => x.roleId === roleId);
                const staffForRoleByCrewId = _.groupBy(staffWithMatchingRole, 'crewId');
                return Object.keys(staffForRoleByCrewId).map((x) => {
                    return { id: x, name: this.crewIdToNameMap[x], staff: staffForRoleByCrewId[x] };
                });
            };
        },
        noCrewSelected() {
            return this.crewIds.length === 0;
        },
        wrappedEquipment() {
            return this.equipment.map((x) => {
                const wrapped = {
                    ...x,
                    secondaryEquipment: this.getSecondaryEquipmentByEquipment(x.id),
                    staff: this.getStaffForEquipment(x.id),
                    tasks: this.getTasksWithEquipmentByShift(x.id, x.equipmentId)
                };
                return wrapped;
            });
        },
        wrappedEquipmentLocations() {
            const shiftStartTime = useShiftDetails().shiftStartTime;
            const shiftEndTime = useShiftDetails().shiftEndTime;

            return this.headings.map(heading => ({
                ...heading.location,
                hazards: heading.shiftLocationDetails?.hazardNotes ?? [],
                comments: heading.shiftLocationDetails?.comments ?? null,
                plannedLocationId: heading.id,
                tasks: heading.tasks.filter((task) => {
                    return !task.isDelay && task.endTime.isAfter(shiftStartTime) && task.startTime.isBefore(shiftEndTime);
                }).map(task => ({
                    ...this.enrichTaskWithLevelledResourceInfo(task),
                    staff: task.staffIds.map(staffId => this.getStaffWithLevelledResourceInfo(staffId))
                }))
            })).filter(x=>x.tasks.length > 0);
        },
        wrappedLocations() {
            const shiftStartTime = useShiftDetails().shiftStartTime;
            const shiftEndTime = useShiftDetails().shiftEndTime;

            return this.headings.map(heading => ({
                ...heading.location,
                hazards: heading.shiftLocationDetails?.hazardNotes ?? [],
                comments: heading.shiftLocationDetails?.comments ?? null,
                plannedLocationId: heading.id,
                equipmentInfo: this.wrappedEquipment.filter(equip => equip.tasks.some(task => task.locationId === heading.location.id)),
                tasks: heading.tasks.filter((task) => {
                    return !task.isDelay && task.endTime.isAfter(shiftStartTime) && task.startTime.isBefore(shiftEndTime);
                }).map(task => ({
                    ...this.enrichTaskWithLevelledResourceInfo(task),
                    staff: task.staffIds.map(staffId => this.getStaffWithLevelledResourceInfo(staffId))
                }))
            })).filter(x=>x.tasks.length > 0);
        },
        groupedEquipment() {
            return [
                { name: this.$t('resources.primary-term', { term: this.$termSync('Equipment') }), translationKey: 'heading.primary-equipment', data: this.filteredPrimaryEquip, showHeader: this.primaryEquip.length > 0, showUnassigned: this.showUnassignedEquipment.primary, setShowUnassigned: (value) => this.showUnassignedEquipment.primary = value},
                {
                    name: this.$t('resources.secondary-term', { term: this.$termSync('Equipment') }),
                    translationKey: 'heading.secondary-equipment',
                    data: this.filteredSecondaryEquip,
                    showHeader: this.secondaryEquip.length > 0,
                    showUnassigned: this.showUnassignedEquipment.secondary,
                    setShowUnassigned: (value) => this.showUnassignedEquipment.secondary = value
                },
                { name: this.$t('resources.support-term', { term: this.$termSync('Equipment') }), translationKey: 'heading.support-equipment', data: this.filteredSupportEquip, showHeader: this.supportEquip.length > 0, showUnassigned: this.showUnassignedEquipment.support, setShowUnassigned: (value) => this.showUnassignedEquipment.support = value }
            ];
        },
        primaryEquip() {
            if (!this.wrappedEquipment) return [];
            var equipment = this.wrappedEquipment.filter((x) => IsPrimary(x));
            return equipment.sort(SortByRoleThenName);
        },
        filteredPrimaryEquip() {
            var searchFilter = (this.equipmentFilter || '').toLowerCase();
            return this.primaryEquip.filter((x) =>this.filterEquipment(x, searchFilter, this.showUnassignedEquipment.primary));
        },
        secondaryEquip() {
            if (!this.wrappedEquipment) return [];
            var equipment = this.wrappedEquipment.filter((x) => IsSecondary(x));
            return equipment.sort(SortByRoleThenName);
        },
        filteredSecondaryEquip() {
            var searchFilter = (this.equipmentFilter || '').toLowerCase();
            return this.secondaryEquip.filter((x) =>this.filterEquipment(x, searchFilter, this.showUnassignedEquipment.secondary));
        },
        supportEquip() {
            if (!this.wrappedEquipment) return [];
            var equipment = this.wrappedEquipment.filter((x) => IsSupport(x));
            return equipment.sort(SortByRoleThenName);
        },
        filteredSupportEquip() {
            var searchFilter = (this.equipmentFilter || '').toLowerCase();
            return this.supportEquip.filter((x) =>this.filterEquipment(x, searchFilter, this.showUnassignedEquipment.support));
        },
        crewSelect() {
            if (this.weekCrews == null) {
                return [];
            }
            let infoMsg = [];
            infoMsg.push({
                header: this.$t('resources.changing-crews-will-clear-assigned-roles')
            });
            infoMsg.push({
                divider: true
            });
            let crewSelects = this.weekCrews.map((x) => ({ id: x.id, name: x.crew.name }));
            return infoMsg.concat(crewSelects);
        },
        filteredSortedEquipment() {
            var groups = {};

            for (let x in this.equipmentRoles) {
                if (this.equipmentRoles.hasOwnProperty(x)) {
                    groups[this.equipmentRoles[x].id] = { equipmentRole: this.equipmentRoles[x], equipment: [] };
                }
            }

            groups['Unknown'] = {
                equipmentRole: {
                    name: 'Uncategorised'
                },
                equipment: []
            };

            var searchFilter = (this.secondaryEquipmentFilter || '').toLowerCase();
            for (let equip of this.wrappedEquipment) {
                if (IsPrimary(equip)) continue;
                if (!this.filterEquipment(equip, searchFilter, true)) continue;
                if (equip.role.id && groups[equip.role.id]) {
                    groups[equip.role.id].equipment.push(equip);
                } else {
                    groups['Unknown'].equipment.push(equip);
                }
            }

            var ret = [];
            for (let x in groups) {
                ret.push(groups[x]);
            }

            return ret;
        },
        staffHasBeenAssignedToResources() {
            return this.wrappedEquipment.some((x) => x.staffIds.length > 0) || this.wrappedEquipmentLocations.some(x=>x.tasks.some(t=>t.staffIds.length > 0));
        },
        utilisationStatsDisplay() {
            return {
                groupTitle: this.$t('resources.resource-utilisation'),
                utilisationFigures: [
                    {
                        ...this.crewUtilisation,
                        helpText: this.$t('resources.percentage-utilisation-of-crew-members'),
                        shortTitle: 'C'
                    },
                    {
                        ...this.primaryEquipmentUtilisation,
                        helpText: this.$t('resources.primary-equipment-utilisation', { term: this.$termSync("equipment") }).toString(),
                        shortTitle: 'P'
                    },
                    {
                        ...this.secondaryEquipmentUtilisation,
                        helpText: this.$t('resources.secondary-equipment-utilisation', { term: this.$termSync("equipment") }).toString(),
                        shortTitle: 'S'
                    },
                    {
                        ...this.supportEquipmentUtilisation,
                        helpText: this.$t('resources.support-equipment-utilisation', { term: this.$termSync("equipment") }).toString(),
                        shortTitle: 'Sp'
                    }
                ]
            };
        },
        staffRoleTooltimeLimits() {
            return _.flatMap(this.levelledResources, x => x.staffRoleIds.map(r => ({
                staffRoleId: r,
                tooltimeLimitMinutes: x.tooltimeMinutesPerShift
            })));
        },
        draggingStaffWithLevelledResource() {
            if(!this.dragging) return false;

            if(this.dragSource === DragSourceType.STAFF && this.dragData?.staff?.id != null) {
                return this.getStaffWithLevelledResourceInfo(this.dragData.staff.id);
            }

            return null;
        },
        draggingLevelledResource() {
            return this.draggingStaffWithLevelledResource?.levelledResource ?? null;
        },
        dragging() {
            return this.dragSource != null;
        },
        dropTargetId() {
            return this.dropTarget?.id ?? null;
        },
        secondaryDropTargetId() {
            return this.dropTarget?.secondaryId ?? null;
        },
        dropTargetType() {
            return this.dropTarget?.type ?? null;
        },
        shiftCommsRoles() {
            return this.shiftCommunication.fieldValues.filter(x => x.fieldTemporalLevel == CommsFieldTemporalLevel.Shift && x.fieldDataType == CommsFieldDataType.Role).sort((a, b) => a.fieldDisplayOrder - b.fieldDisplayOrder);
        },
        staffNamesAssignableToRoles() {
            return _.uniq(this.staffMembers?.filter((x) => !x.isArchived).map(x => x.name) ?? []).sort((a, b) => a.localeCompare(b));
        },
        hasShiftCommsRoles() {
            return this.shiftCommsRoles.length > 0;
        },
        hasVisibleEquipmentLocations() {
            return this.wrappedEquipmentLocations.length > 0;
        },
        hasVisibleLocationsWithEquipment() {
            return this.wrappedLocations.length > 0;
        },
        showEquipmentLocations() {
            return DepartmentHasFeature(this.departmentFeatures, DepartmentFeatureFlags.Maintenance);
        },
        showEquipmentGroupedByLocation() {
            return DepartmentHasFeature(this.departmentFeatures, DepartmentFeatureFlags.LocationsInResourceBoard);
        },
        showClearStaffAssignments() {
            return this.crewIds.length > 0 && this.staffHasBeenAssignedToResources;
        },
        showCopyPreviousButton() {
            return !DepartmentHasFeature(this.departmentFeatures, DepartmentFeatureFlags.Maintenance);
        },
    },
    methods: {
        ...mapActions(useShiftEquipment, [
            'addStaffToEquipment',
            'removeSecondaryEquipment',
            'addSecondaryEquipment',
            'setEquipmentLocation',
            'setEquipmentComments',
            'removeAllStaffFromEquipment',
            'removeStaffFromEquipment',
            'markEquipmentAsInWorkshop',
            'markEquipmentAsNotInWorkshop',
            'markEquipmentAsNeededInWorkshop',
            'markEquipmentAsNotNeededInWorkshop'
        ]),
        ...mapActions(useShiftCrew, [
            'changeSelectedCrews',
            'addStaffMemberToShift',
            'addRoleToShift',
            'removeStaffMemberFromShift',
            'calculateStaffUtilisation'
        ]),
        ...mapActions(useSystemStore, ['scheduleUnplannedLeave', 'addStaffMember']),
        ...mapActions(useShiftDetails, ['populateCrewAndEquipmentUtilisationScores']),
        ...mapActions(useShiftCommunication, ['getShiftCommunication', 'updateShiftCommunication']),
        ...mapActions(useHeadingsStore, ['executeTaskCommand', 'getHeadingById']),
        onOpenEquipmentServices(equipmentId) {
            this.selectedEquipmentAvailability = this.equipmentById(equipmentId);
        },
        onOpenEquipmentHazards(equipmentId) {
            this.selectedHazardEquipment = this.equipmentById(equipmentId);
        },
        onCloseEquipmentHazards() {
            this.selectedHazardEquipment = null;
        },
        onOpenLocationHazards(plannedLocationId) {
            this.selectedHazardLocation = this.getHeadingById(plannedLocationId);
        },
        onCloseLocationHazards() {
            this.selectedHazardLocation = null;
        },
        async toggleInWorkshop(equipmentId, inWorkshop) {
            if (inWorkshop) {
                await this.markEquipmentAsInWorkshop(equipmentId);
            } else {
                await this.markEquipmentAsNotInWorkshop(equipmentId);
            }
        },
        async toggleNeededInWorkshop(equipmentId, neededInWorkshop) {
            if (neededInWorkshop) {
                await this.markEquipmentAsNeededInWorkshop(equipmentId);
            } else {
                await this.markEquipmentAsNotNeededInWorkshop(equipmentId);
            }
        },
        filterEquipment(e, searchFilter, showUnassigned) {
            if (e.idleOrUnavailable) return false;
            if (this.hideUnavailableEquipment && e.availability && e.availability.length > 0) return false;
            if(!showUnassigned && e.tasks.length === 0) return false;
            if (!searchFilter) return true;
            if ((e.name || '').toLowerCase().indexOf(searchFilter) > -1) return true;
            if (!e.role) return true;
            if ((e.role.name || '').toLowerCase().indexOf(searchFilter) > -1) return true;
        },
        getActiveTargetTask(locationId) {
            return this.dragSource === DragSourceType.STAFF && this.dropTargetType === DropTargetType.TASK && this.secondaryDropTargetId === locationId ? this.dropTarget?.id : null;
        },
        onAddRoleDropTarget(roleId) {
            setTimeout(() => {
                this.dropTarget = { type: DropTargetType.ROLE, id: roleId };
            }, 1);
        },
        onAddEquipmentDropTarget(equipmentId) {
            setTimeout(() => {
                this.dropTarget = { type: DropTargetType.EQUIPMENT, id: equipmentId };
            }, 1);
        },
        onAddLocationDropTarget(locationId) {
            setTimeout(() => {
                this.dropTarget = { type: DropTargetType.LOCATION, id: locationId };
            }, 1);
        },
        onAddLocationTaskDropTarget(locationId, taskId) {
            setTimeout(() => {
                this.dropTarget = { type: DropTargetType.TASK, id: taskId, secondaryId: locationId };
            }, 1);
        },
        onRemoveDropTarget(targetId) {
            if (this.dropTarget === targetId) {
                setTimeout(() => {
                    this.dropTarget = null;
                }, 1);
            }
        },
        debouncedUpdateShiftSelectedCrews: _.debounce(function(crewIds) {
            this.updateShiftSelectedCrews(crewIds);
        }, 1000),
        async updateShiftSelectedCrews(crewIds) {

            this.$wait.start('set_selected_crews');
            this.$refs.crewSelect.blur();
            try {
                await this.changeSelectedCrews(crewIds);
            } finally {
                this.$wait.end('set_selected_crews');
            }

            this.$wait.start('copy equipment allocations - info');
            try {
                await this.refreshCopyPreviousAllocationsInfo();
            } finally {
                this.$wait.end('copy equipment allocations - info');
            }
        },
        clearDropTarget($event) {
            this.dropTarget = null;
        },
        async refreshCopyPreviousAllocationsInfo() {
            if (this.crewIds.length === 0) {
                this.copyPreviousAllocationsInfo = null;
            } else {
                this.copyPreviousAllocationsInfo = await WeekCrews.getShiftEquipmentCopyInfo(this.shiftId, this.crewIds);
            }
        },
        async copyPreviousAllocation() {
            this.copyPreviousAllocationsMenu = false;

            if (this.copyPreviousAllocationsInfo == null || !this.copyPreviousAllocationsInfo.canCopy) {
                return;
            }

            try {
                this.$wait.start('copy equipment allocations');
                await WeekCrews.copyAllocationsFromPreviousShift(this.shiftId, this.copyPreviousAllocationsInfo.copyShiftId);
                EventBus.$emit(Events.ShiftRefreshRequested);
                EventBus.$emit(Events.ToastSuccess, this.$t('resources.message.copy-previous'));
            } catch (e) {
                EventBus.$emit(Events.ToastError, e);
            } finally {
                this.$wait.end('copy equipment allocations');
            }
        },
        async loadData() {
            try {
                this.$wait.start('get data');

                this.mine = {
                    activeShiftSchedule: this.activeShiftSchedule,
                    firstShiftStartsAt: this.firstShiftStartsAt,
                    availableShifts: this.availableShifts,
                    firstDayOfWeek: this.firstDayOfWeek,
                    locale: this.locale,
                    isSandpit: this.isSandpit
                };

                this.calculateStaffUtilisation(false);
                await this.refreshCopyPreviousAllocationsInfo();
            } finally {
                this.$wait.end('get data');
            }
        },
        addCrewRole() {
            const rolesNotInTemplate = this.staffRoles.filter(
                (role) => this.roles.some((shiftRole) => shiftRole.id === role.id) === false
            );
            this.$refs.addCrewRole.show(this.$route.params.dept, this.shiftId, this.crewIds, rolesNotInTemplate);
        },
        async clearStaffAssignments() {
            let confirmResult = await this.confirmClearStaffAssignments();
            if (!confirmResult) {
                return;
            }

            try {
                this.$wait.start('clear equipment assignment');
                var command = {
                    departmentShiftId: this.shiftId
                };

                this.removeAllStaffFromEquipment();
                await this.removeAllStaffFromTasks();
                var result = await WeekCrews.clearStaffAssignmentsForShift(command);

                await this.calculateStaffUtilisation(true);

                EventBus.$emit(Events.ToastSuccess, this.$t('resources.message.clear-staff-confirmation'));
            } catch (e) {
                Logging.error(e);
            } finally {
                this.$wait.end('clear equipment assignment');
            }
        },
        async confirmClearStaffAssignments() {
            // @ts-ignore
            return await this.$refs.confirm.open(
                this.$t('resources.heading.clear-staff-confirm-prompt'),
                this.$t('resources.message.clear-staff-confirm-prompt'),
                { color: 'red' }
            );
        },
        startDragEquipment() {
            this.startDrag(DragSourceType.EQUIPMENT);
        },
        startDragStaff(dragData) {
            this.dragData = dragData;
            this.startDrag(DragSourceType.STAFF);
        },
        startDrag(dragSource) {
            this.dragSource = dragSource;
        },
        endDrag() {
            this.dragSource = null;
            setTimeout(() => this.clearDropTarget(), 1);
        },
        async onUnplannedLeaveScheduled(staffId, items) {
            await this.scheduleUnplannedLeave(staffId, items);
        },
        async onAddSecondaryEquipmentToEquipment(equipment, secondaryEquipmentId) {
            await this.addSecondaryEquipment(equipment.id, secondaryEquipmentId);
            await this.calculateStaffUtilisation(true);
        },
        async onRemoveSecondaryEquipmentFromEquipment(equipment, secondaryEquipmentId) {
            await this.removeSecondaryEquipment(equipment.id, secondaryEquipmentId);
            await this.calculateStaffUtilisation(true);
        },
        async onEquipmentTouchDrop(data) {
            await this.addSecondaryEquipment(this.dropTarget.id, data.equipment.id);
        },
        async onStaffTouchDrop(data) {
            if (this.dropTarget == null)
                return;

            if (this.dropTarget.type === DropTargetType.ROLE) {
                await this.onStaffDroppedOnRole(data, this.dropTarget.id);
            } else if (this.dropTarget.type === DropTargetType.EQUIPMENT) {
                await this.onStaffDroppedOnEquipment(data, this.dropTarget);
            }
        },
        async onStaffDroppedOnEquipment(data, dropTarget) {
            await this.addStaffToEquipment(dropTarget.id, data.staff.id);
            await this.calculateStaffUtilisation(true);
        },
        async onStaffDroppedOnRole(data, roleFieldId) {
            await this.onAddStaffToRole(roleFieldId, data.staff.name);
        },
        async onStaffDroppedOnLocation(data, locationId) {
            await this.onAddStaffToLocation(locationId, data.staff.id);
        },
        async onStaffDroppedOnTask(data, taskId, locationId) {
            await this.onAddStaffToTask(taskId, locationId, data.staff.id);
        },
        async onAdditionalRoleSelected(roleId) {
            await this.addRoleToShift(roleId);
        },
        async onAddNewStaffMember(staffMember) {
            await this.addStaffMember({ ...staffMember, isSandpitUseOnly: staffMember.crew.crew.isSandpitUseOnly });
            await this.addStaffMemberToShift(staffMember.id, staffMember.staffRoleId);
        },
        async onAdditionalStaffSelected(staffId, roleId) {
            await this.addStaffMemberToShift(staffId, roleId);
        },
        async onAddCrewMember(staffMember) {
            await this.addStaffMemberToShift(staffMember.id, staffMember.staffRoleId);
        },
        async onRemoveCrewMember(staffMember) {
            await this.removeStaffMemberFromShift(staffMember.id);
        },
        async onAddStaffToRole(roleFieldId, staffName) {
            var command = {
                _type: 'UpdateShiftCommunicationsCommand',
                isOfflineReplay: false,
                attemptedAt: new Date(),
                shiftId: this.shiftId,
                fieldValues: [{ commsFieldId: roleFieldId, value: staffName, _type: 'UpdateCommsFieldDto' }]
            };
            await Shift.updateShiftCommunications(ModelBuilder.Commands.UpdateShiftCommunicationsCommand(command));
            this.updateShiftCommunication(command);
        },
        async onAddStaffToLocation(plannedLocationId, staffId) {
            const staffMember = this.getStaffWithLevelledResourceInfo(staffId);

            if (staffMember == null)
                return;

            if (staffMember.levelledResource == null) {
                EventBus.$emit(Events.ToastError, this.$t('resources.staff-member-no-levelled-resource'));
                return;
            }

            const taskIds = this.wrappedEquipmentLocations.find(x => x.plannedLocationId === plannedLocationId)?.tasks?.filter(x => x.levelledResource?.id === staffMember.levelledResource.id)?.map(x => x.id) ?? [];

            if (taskIds.length == 0) {
                EventBus.$emit(Events.ToastError, this.$t('resources.staff-member-no-matching-tasks'));
                return;
            }

            const command = {
                _type: 'AddStaffToTasksCommand',
                plannedTaskIds: taskIds,
                staffId,
                plannedLocationId,
                departmentId: this.departmentId,
                shiftId: this.shiftId
            };
            await this.executeTaskCommand(command);
            await this.calculateStaffUtilisation(true);
        },
        async onAddStaffToTask(taskId, plannedLocationId, staffId) {
            const command = {
                _type: 'AddStaffToTaskCommand',
                plannedTaskId: taskId,
                staffId,
                plannedLocationId,
                departmentId: this.departmentId,
                shiftId: this.shiftId
            };
            await this.executeTaskCommand(command);
            await this.calculateStaffUtilisation(true);
        },
        async onAddStaffToEquipment(equipmentId, staffId) {
            await this.addStaffToEquipment(equipmentId, staffId);
            await this.calculateStaffUtilisation(true);
        },
        async onRemoveStaffFromTask(taskId, plannedLocationId, staffId){
            const command = {
                _type: 'RemoveStaffFromTaskCommand',
                plannedTaskId: taskId,
                staffId,
                plannedLocationId,
                departmentId: this.departmentId,
                shiftId: this.shiftId,
            };
            await this.executeTaskCommand(command);
            await this.calculateStaffUtilisation(true);
        },
        async onRemoveStaffFromLocation(plannedLocationId, staffId){
            const taskIds = this.wrappedEquipmentLocations.find(x=>x.plannedLocationId === plannedLocationId)?.tasks?.map(x=>x.id) ?? [];

            if(taskIds.length === 0)
                return;

            const command = {
                _type: 'RemoveStaffFromTasksCommand',
                plannedTaskIds: taskIds,
                staffId,
                plannedLocationId,
                departmentId: this.departmentId,
                shiftId: this.shiftId,
            };
            await this.executeTaskCommand(command);
            await this.calculateStaffUtilisation(true);
        },
        async removeAllStaffFromTasks() {
            const plannedTaskIds = _.flatMap(this.wrappedEquipmentLocations, x=>x.tasks.filter(t=>t.staff.length > 0).map(t=>t.id));

            if(plannedTaskIds.length === 0)
                return;

            const commands = _.flatMap(this.wrappedEquipmentLocations.filter(x=>x.tasks.some(t=>t.staff.length > 0)), x=>({
                _type: 'RemoveAllStaffFromTasksCommand',
                plannedLocationId: x.plannedLocationId,
                plannedTaskIds: x.tasks.filter(t=>t.staff.length > 0).map(t=>t.id),
                departmentId: this.departmentId,
                shiftId: this.shiftId
            }));

            for(let command of commands){
                await this.executeTaskCommand(command);
            }
        },
        async onRemoveStaffFromEquipment(equipmentId, staffId) {
            await this.removeStaffFromEquipment(equipmentId, staffId);
            await this.calculateStaffUtilisation(true);
        },
        async setEquipLocationFor(equipmentId, location) {
            await this.setEquipmentLocation(equipmentId, location);
        },
        async setEquipCommentsFor(equipmentId, comment) {
            await this.setEquipmentComments(equipmentId, comment);
        },
        async onChangeTaskComment(plannedLocationId, taskId, comment){
            const task = this.wrappedEquipmentLocations.find(x=>x.plannedLocationId === plannedLocationId)?.tasks?.find(x=>x.id === taskId);

            if(task == null)
                throw new Error(this.$t('resources.task-not-found', { location: plannedLocationId, task: taskId }).toString());

            const command = BuildUpdateAdHocTaskCommandFromTask(task, this.shiftId, plannedLocationId);
            command.description = comment;

            await this.executeTaskCommand(command);
        },
        async onChangeLocationComment(plannedLocationId, comment){
            const location = this.getHeadingById(plannedLocationId);
            const updatedDetails = {
                ...location.shiftLocationDetails,
                comments: comment
            }

            var command = ModelBuilder.Commands.UpdateShiftLocationDetailsCommand({
                ...updatedDetails
            });
            useShiftDetails().updateShiftLocationDetails(plannedLocationId, command);
            await ShiftLocationDetails.send(command);
        },
        manuallyAddCrew(role) {
            this.$refs.addAnyStaff.show(
                this.$route.params.dept,
                this.shiftId,
                this.crewIds,
                role,
                this.staffMembers.filter(s => !s.isArchived && this.mine.isSandpit === s.isSandpitUseOnly),
                this.weekCrews,
                this.staffRoles.filter(sr => this.mine.isSandpit === sr.isSandpitUseOnly),
                this.$route.params.date
            );
        },
        viewLeaveFor(member, alwaysLookAtStaffArray) {
            var staffAvailability = member.staffAvailability;
            if ((alwaysLookAtStaffArray || !staffAvailability) && this.staffMembers) {
                for (let x of this.staffMembers) {
                    if (x.id == member.id) {
                        staffAvailability = x.staffAvailability;
                        break;
                    }
                }
            }
            this.$refs.viewLeaveOvertime.show(
                member,
                staffAvailability || [],
                this.weekDate,
                this.mine,
                this.$route.params.date,
                this.$route.params.shift
            );
        },
        enrichTaskWithLevelledResourceInfo(task) {
            const levelledResource = this.levelledResources.find(x => x.taskTypeIds.includes(task.taskTypeId)) ?? null;

            return {
                ...task,
                levelledResource: levelledResource
            };
        },
        getStaffWithLevelledResourceInfo(staffId) {
            const staff = this.getStaffById(staffId);

            if (staff == null)
                return staff;

            const levelledResource = this.levelledResources.find(x => x.staffRoleIds.includes(staff.staffRoleId)) ?? null;
            return { ...staff, levelledResource };
        },
        async onSaveAvailability() {
            this.selectedEquipmentAvailability = null;
        },
        enablePageFeatures() {
            EventBus.$emit(Events.ShowStats, this.utilisationStatsDisplay);
        },
        disablePageFeatures() {
            EventBus.$emit(Events.ShowStats, null);
        }
    }
};
</script>

<style lang='scss' scoped>
@import '@/variables.scss';

@media print {
    .page-layout > .col-6 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
    }
    .v-card {
        display: block;
        page-break-inside: avoid !important;
    }
    .v-card .row {
        display: flex;
    }
}

.clear-bottom {
    padding-bottom: 80px !important;
}

.dropMagnet {
    background-color: #f2f2f2;
}

.mid-col {
    border-right: 1px solid #ddd;
    border-left: 1px solid #ddd;
}

.highlighted {
    box-shadow: 0px 0px 2px 2px #3333;
}

.striped {
    background-color: $stripe-color;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
